import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import PageBreadcrumb from '../../components/common/PageBreadCrumb';
import ComponentCard from '../../components/common/ComponentCard';
import PageMeta from '../../components/common/PageMeta';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '../../components/ui/table';
import dayjs from 'dayjs';
import { FamilyMember, useFamilyMemberStore } from '../../store/useFamilyMemberStore';
import { useFamilyStore } from '../../store/useFamilyStore';
import { Button, Label, Modal, Popover, TextInput, ToggleSwitch, Select } from 'flowbite-react';
import { HiOutlineExclamationCircle, HiX, HiPlus } from 'react-icons/hi';
import toast from 'react-hot-toast';

export default function FamilyMembersPage() {
  const navigate = useNavigate();
  const getFamilyMembers = useFamilyMemberStore((state) => state.getFamilyMembers);
  const deleteFamilyMember = useFamilyMemberStore((state) => state.deleteFamilyMember);
  const members = useFamilyMemberStore((state) => state.members);
  const createFamilyMember = useFamilyMemberStore((state) => state.createFamilyMember);
  const updateFamilyMember = useFamilyMemberStore((state) => state.updateFamilyMember);
  const loading = useFamilyMemberStore((state) => state.loading);
  const pagination = useFamilyMemberStore((state) => state.pagination);
  const getFamilies = useFamilyStore((state) => state.getFamilies);
  const families = useFamilyStore((state) => state.families);

  const [tableData, setTableData] = useState<FamilyMember[]>([]);
  const [search, setSearch] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [limit] = useState<number>(10);
  const [openModal, setOpenModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState<string | null>(null);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [modalType, setModalType] = useState<string>('add');
  const [editId, setEditId] = useState<string | null>(null);
  const [selectedFamily, setSelectedFamily] = useState<string>('');
  const [filterFamily, setFilterFamily] = useState<string>('');

  const [name, setName] = useState('');
  const [dob, setDob] = useState('');
  const [education, setEducation] = useState('');
  const [job, setJob] = useState('');
  const [baptismDate, setBaptismDate] = useState('');
  const [firstCommunionDate, setFirstCommunionDate] = useState('');
  const [confirmationDate, setConfirmationDate] = useState('');
  const [married, setMarried] = useState(false);
  const [marriedDate, setMarriedDate] = useState('');
  const [deathDate, setDeathDate] = useState('');
  const [contactNumbers, setContactNumbers] = useState<string[]>(['']);
  const [mother, setMother] = useState<string>('');
  const [father, setFather] = useState<string>('');

  useEffect(() => {
    getFamilies();
  }, [getFamilies]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, filterFamily]);

  useEffect(() => {
    getFamilyMembers(filterFamily || undefined, search || undefined, currentPage, limit);
  }, [getFamilyMembers, filterFamily, search, currentPage, limit]);

  useEffect(() => {
    setTableData(members);
  }, [members]);

  const handleAddOrEdit = async () => {
    if (!name || !selectedFamily) {
      toast.error('Please fill in all required fields (Name and Family).', {
        duration: 2000,
        icon: '❌',
      });
      return;
    }

    const memberData = {
      name,
      dob: dob || undefined,
      education: education || undefined,
      job: job || undefined,
      baptismDate: baptismDate || undefined,
      firstCommunionDate: firstCommunionDate || undefined,
      confirmationDate: confirmationDate || undefined,
      married,
      marriedDate: marriedDate || undefined,
      deathDate: deathDate || undefined,
      contactNumbers: contactNumbers.filter((cn) => cn.trim() !== ''),
      mother: mother || undefined,
      father: father || undefined,
      family: selectedFamily,
    };

    if (modalType === 'add') {
      await createFamilyMember(memberData);
    } else {
      if (!editId) return;
      await updateFamilyMember(editId, memberData);
      // Refresh with current filter and pagination after update
      const currentPagination = useFamilyMemberStore.getState().pagination;
      await getFamilyMembers(filterFamily || undefined, search || undefined, currentPagination?.page, currentPagination?.limit);
    }

    setOpenAddModal(false);
    resetForm();
  };

  const resetForm = () => {
    setName('');
    setDob('');
    setEducation('');
    setJob('');
    setBaptismDate('');
    setFirstCommunionDate('');
    setConfirmationDate('');
    setMarried(false);
    setMarriedDate('');
    setDeathDate('');
    setContactNumbers(['']);
    setMother('');
    setFather('');
    setSelectedFamily('');
    setEditId(null);
    setModalType('add');
  };

  const handleEdit = async (member: FamilyMember) => {
    setEditId(member._id);
    setModalType('edit');
    setOpenAddModal(true);
    setName(member.name);
    setDob(member.dob || '');
    setEducation(member.education || '');
    setJob(member.job || '');
    setBaptismDate(member.baptismDate || '');
    setFirstCommunionDate(member.firstCommunionDate || '');
    setConfirmationDate(member.confirmationDate || '');
    setMarried(member.married || false);
    setMarriedDate(member.marriedDate || '');
    setDeathDate(member.deathDate || '');
    setContactNumbers(member.contactNumbers && member.contactNumbers.length > 0 ? member.contactNumbers : ['']);
    const motherId = typeof member.mother === 'string' ? member.mother : member.mother?._id || '';
    const fatherId = typeof member.father === 'string' ? member.father : member.father?._id || '';
    setMother(motherId);
    setFather(fatherId);
    const familyId = typeof member.family === 'string' ? member.family : member.family?._id || '';
    setSelectedFamily(familyId);
  };

  const handleDelete = () => {
    if (selectedMember !== null) {
      deleteFamilyMember(selectedMember);
      setOpenModal(false);
      setSelectedMember(null);
    }
  };

  const addContactNumber = () => {
    setContactNumbers([...contactNumbers, '']);
  };

  const removeContactNumber = (index: number) => {
    setContactNumbers(contactNumbers.filter((_, i) => i !== index));
  };

  const updateContactNumber = (index: number, value: string) => {
    const updated = [...contactNumbers];
    updated[index] = value;
    setContactNumbers(updated);
  };

  return (
    <>
      <Modal
        show={openModal}
        size="md"
        position="center"
        onClose={() => {
          setOpenModal(false);
          setSelectedMember(null);
        }}
        popup
      >
        <Modal.Header />
        <Modal.Body>
          <div className="text-center">
            <HiOutlineExclamationCircle className="mx-auto mb-4 h-14 w-14 text-gray-400 dark:text-gray-200" />
            <h3 className="mb-5 text-lg font-normal text-gray-500 dark:text-gray-400" style={{ fontFamily: 'Lato, sans-serif' }}>
              Are you sure you want to delete this family member?
            </h3>
            <div className="flex justify-center gap-4">
              <Button
                onClick={handleDelete}
                className="px-6 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium flex items-center justify-center"
                style={{ fontFamily: 'Lato, sans-serif', height: '36px' }}
              >
                {"Yes, I'm sure"}
              </Button>
              <Button
                onClick={() => {
                  setOpenModal(false);
                  setSelectedMember(null);
                }}
                className="px-6 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium flex items-center justify-center"
                style={{ fontFamily: 'Lato, sans-serif', height: '36px' }}
              >
                No, cancel
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        show={openAddModal}
        size="6xl"
        popup
        onClose={() => {
          setOpenAddModal(false);
          resetForm();
        }}
      >
        <Modal.Header />
        <Modal.Body>
          <div className="space-y-6 max-h-[85vh] overflow-y-auto">
            <h3 className="text-xl font-medium text-gray-900 dark:text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
              {modalType === 'add' ? 'Add Family Member' : 'Edit Family Member'}
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="mb-2 block">
                  <Label htmlFor="name" value="Name *" style={{ fontFamily: 'Lato, sans-serif' }} />
                </div>
                <TextInput
                  id="name"
                  type="text"
                  placeholder="Enter name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  style={{ fontFamily: 'Lato, sans-serif' }}
                />
              </div>

              <div>
                <div className="mb-2 block">
                  <Label htmlFor="family" value="Family *" style={{ fontFamily: 'Lato, sans-serif' }} />
                </div>
                <Select
                  id="family"
                  value={selectedFamily}
                  onChange={(e) => setSelectedFamily(e.target.value)}
                  style={{ fontFamily: 'Lato, sans-serif' }}
                >
                  <option value="">Select Family</option>
                  {families.map((family) => (
                    <option key={family._id} value={family._id}>
                      {family.familyName}
                    </option>
                  ))}
                </Select>
              </div>

              <div>
                <div className="mb-2 block">
                  <Label htmlFor="dob" value="Date of Birth" style={{ fontFamily: 'Lato, sans-serif' }} />
                </div>
                <TextInput id="dob" type="date" value={dob} onChange={(e) => setDob(e.target.value)} style={{ fontFamily: 'Lato, sans-serif' }} />
              </div>

              <div>
                <div className="mb-2 block">
                  <Label htmlFor="education" value="Education" style={{ fontFamily: 'Lato, sans-serif' }} />
                </div>
                <TextInput
                  id="education"
                  type="text"
                  placeholder="Enter education"
                  value={education}
                  onChange={(e) => setEducation(e.target.value)}
                  style={{ fontFamily: 'Lato, sans-serif' }}
                />
              </div>

              <div>
                <div className="mb-2 block">
                  <Label htmlFor="job" value="Job" style={{ fontFamily: 'Lato, sans-serif' }} />
                </div>
                <TextInput
                  id="job"
                  type="text"
                  placeholder="Enter job"
                  value={job}
                  onChange={(e) => setJob(e.target.value)}
                  style={{ fontFamily: 'Lato, sans-serif' }}
                />
              </div>

              <div>
                <div className="mb-2 block">
                  <Label htmlFor="baptismDate" value="Baptism Date" style={{ fontFamily: 'Lato, sans-serif' }} />
                </div>
                <TextInput
                  id="baptismDate"
                  type="date"
                  value={baptismDate}
                  onChange={(e) => setBaptismDate(e.target.value)}
                  style={{ fontFamily: 'Lato, sans-serif' }}
                />
              </div>

              <div>
                <div className="mb-2 block">
                  <Label htmlFor="firstCommunionDate" value="First Communion Date" style={{ fontFamily: 'Lato, sans-serif' }} />
                </div>
                <TextInput
                  id="firstCommunionDate"
                  type="date"
                  value={firstCommunionDate}
                  onChange={(e) => setFirstCommunionDate(e.target.value)}
                  style={{ fontFamily: 'Lato, sans-serif' }}
                />
              </div>

              <div>
                <div className="mb-2 block">
                  <Label htmlFor="confirmationDate" value="Confirmation Date" style={{ fontFamily: 'Lato, sans-serif' }} />
                </div>
                <TextInput
                  id="confirmationDate"
                  type="date"
                  value={confirmationDate}
                  onChange={(e) => setConfirmationDate(e.target.value)}
                  style={{ fontFamily: 'Lato, sans-serif' }}
                />
              </div>

              <div>
                <div className="mb-2 block">
                  <Label htmlFor="marriedDate" value="Married Date" style={{ fontFamily: 'Lato, sans-serif' }} />
                </div>
                <TextInput
                  id="marriedDate"
                  type="date"
                  value={marriedDate}
                  onChange={(e) => setMarriedDate(e.target.value)}
                  disabled={!married}
                  style={{ fontFamily: 'Lato, sans-serif' }}
                />
              </div>

              <div>
                <div className="mb-2 block">
                  <Label htmlFor="deathDate" value="Death Date" style={{ fontFamily: 'Lato, sans-serif' }} />
                </div>
                <TextInput
                  id="deathDate"
                  type="date"
                  value={deathDate}
                  onChange={(e) => setDeathDate(e.target.value)}
                  style={{ fontFamily: 'Lato, sans-serif' }}
                />
              </div>

              <div>
                <div className="mb-2 block">
                  <Label htmlFor="mother" value="Mother" style={{ fontFamily: 'Lato, sans-serif' }} />
                </div>
                <Select
                  id="mother"
                  value={mother}
                  onChange={(e) => setMother(e.target.value)}
                  style={{ fontFamily: 'Lato, sans-serif' }}
                  disabled={!selectedFamily}
                >
                  <option value="">Select Mother (Optional)</option>
                  {members
                    .filter((m) => {
                      const memberFamilyId = typeof m.family === 'string' ? m.family : m.family?._id || '';
                      return memberFamilyId === selectedFamily && m._id !== editId;
                    })
                    .map((m) => (
                      <option key={m._id} value={m._id}>
                        {m.name}
                      </option>
                    ))}
                </Select>
              </div>

              <div>
                <div className="mb-2 block">
                  <Label htmlFor="father" value="Father" style={{ fontFamily: 'Lato, sans-serif' }} />
                </div>
                <Select
                  id="father"
                  value={father}
                  onChange={(e) => setFather(e.target.value)}
                  style={{ fontFamily: 'Lato, sans-serif' }}
                  disabled={!selectedFamily}
                >
                  <option value="">Select Father (Optional)</option>
                  {members
                    .filter((m) => {
                      const memberFamilyId = typeof m.family === 'string' ? m.family : m.family?._id || '';
                      return memberFamilyId === selectedFamily && m._id !== editId;
                    })
                    .map((m) => (
                      <option key={m._id} value={m._id}>
                        {m.name}
                      </option>
                    ))}
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <ToggleSwitch checked={married} onChange={setMarried} />
                <Label htmlFor="married" value="Married" style={{ fontFamily: 'Lato, sans-serif' }} />
              </div>
            </div>

            <div>
              <div className="mb-2 block">
                <Label htmlFor="contactNumbers" value="Contact Numbers" style={{ fontFamily: 'Lato, sans-serif' }} />
              </div>
              <div className="space-y-2">
                {contactNumbers.map((contact, contactIndex) => (
                  <div key={contactIndex} className="flex gap-2">
                    <TextInput
                      value={contact}
                      onChange={(e) => updateContactNumber(contactIndex, e.target.value)}
                      placeholder="Enter contact number"
                      style={{ fontFamily: 'Lato, sans-serif' }}
                    />
                    {contactNumbers.length > 1 && (
                      <Button
                        onClick={() => removeContactNumber(contactIndex)}
                        size="sm"
                        color="failure"
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      >
                        <HiX />
                      </Button>
                    )}
                  </div>
                ))}
                <Button
                  onClick={addContactNumber}
                  size="sm"
                  className="bg-[#B8860B] text-white hover:bg-[#9A7209]"
                  style={{ fontFamily: 'Lato, sans-serif', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <span style={{ display: 'flex', alignItems: 'center' }}>
                    <HiPlus className="mr-1" /> Add Contact
                  </span>
                </Button>
              </div>
            </div>

            <div className="w-full pt-4 border-t">
              <Button
                onClick={handleAddOrEdit}
                disabled={loading || !name || !selectedFamily}
                className="w-full px-6 bg-[#B8860B] text-white rounded-lg hover:bg-[#9A7209] transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                style={{ fontFamily: 'Lato, sans-serif', height: '36px' }}
              >
                {loading ? 'Processing...' : modalType === 'add' ? 'Create Family Member' : 'Update Family Member'}
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      <PageMeta title="Family Members" description="Manage family members" />
      <PageBreadcrumb pageTitle="Family Members" />
      <div className="space-y-6">
        <ComponentCard title="Family Members">
          <div className="flex flex-col md:flex-row w-full md:justify-start md:items-center gap-5 mb-6">
            <Button
              onClick={() => {
                setOpenAddModal(true);
                setModalType('add');
                resetForm();
              }}
              className="px-6 bg-[#B8860B] text-white rounded-lg hover:bg-[#9A7209] transition-colors font-medium"
              style={{ fontFamily: 'Lato, sans-serif', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              Add Family Member
            </Button>
            <div className="flex-1 max-w-xs">
              <Select value={filterFamily} onChange={(e) => setFilterFamily(e.target.value)} style={{ fontFamily: 'Lato, sans-serif' }}>
                <option value="">All Families</option>
                {families.map((family) => (
                  <option key={family._id} value={family._id}>
                    {family.familyName}
                  </option>
                ))}
              </Select>
            </div>
            <div className="relative">
              <span className="absolute -translate-y-1/2 pointer-events-none left-4 top-1/2">
                <svg
                  className="fill-gray-500 dark:fill-gray-400"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M3.04175 9.37363C3.04175 5.87693 5.87711 3.04199 9.37508 3.04199C12.8731 3.04199 15.7084 5.87693 15.7084 9.37363C15.7084 12.8703 12.8731 15.7053 9.37508 15.7053C5.87711 15.7053 3.04175 12.8703 3.04175 9.37363ZM9.37508 1.54199C5.04902 1.54199 1.54175 5.04817 1.54175 9.37363C1.54175 13.6991 5.04902 17.2053 9.37508 17.2053C11.2674 17.2053 13.003 16.5344 14.357 15.4176L17.177 18.238C17.4699 18.5309 17.9448 18.5309 18.2377 18.238C18.5306 17.9451 18.5306 17.4703 18.2377 17.1774L15.418 14.3573C16.5365 13.0033 17.2084 11.2669 17.2084 9.37363C17.2084 5.04817 13.7011 1.54199 9.37508 1.54199Z"
                    fill=""
                  />
                </svg>
              </span>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                type="text"
                placeholder="Search members..."
                className="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-200 bg-transparent py-2.5 pl-12 pr-14 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-[#B8860B] focus:outline-none focus:ring focus:ring-[#B8860B]/10 dark:border-gray-800 dark:bg-gray-900 dark:bg-white/[0.03] dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-[#B8860B] xl:w-[430px]"
                style={{ fontFamily: 'Lato, sans-serif' }}
              />
            </div>
          </div>

          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
            <div className="max-w-full overflow-x-auto">
              <div className="min-w-[1200px]">
                <Table>
                  <TableHeader className="border-b border-gray-100 dark:border-white/[0.05]">
                    <TableRow>
                      <TableCell
                        isHeader
                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        Name
                      </TableCell>
                      <TableCell
                        isHeader
                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        Family
                      </TableCell>
                      <TableCell
                        isHeader
                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        Date of Birth
                      </TableCell>
                      <TableCell
                        isHeader
                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        Education
                      </TableCell>
                      <TableCell
                        isHeader
                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        Job
                      </TableCell>
                      <TableCell
                        isHeader
                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        Contact Numbers
                      </TableCell>
                      <TableCell
                        isHeader
                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        Created At
                      </TableCell>
                      <TableCell
                        isHeader
                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHeader>

                  <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                    {tableData.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={8}
                          className="px-5 py-8 text-center text-gray-500 dark:text-gray-400"
                          style={{ fontFamily: 'Lato, sans-serif' }}
                        >
                          {loading ? 'Loading...' : 'No family members found'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      tableData.map((member) => (
                        <TableRow key={member._id}>
                          <div
                            className="contents cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 hover:shadow-md transition-all duration-200 group"
                            onClick={() => navigate(`/family-members/${member._id}`)}
                          >
                            <TableCell
                              className="px-4 py-3 text-gray-500 text-theme-sm dark:text-gray-400 group-hover:border-l-4 group-hover:border-[#B8860B] group-hover:text-gray-900 dark:group-hover:text-white transition-all"
                              style={{ fontFamily: 'Lato, sans-serif' }}
                            >
                              {member.name}
                            </TableCell>
                            <TableCell
                              className="px-4 py-3 text-gray-500 text-theme-sm dark:text-gray-400"
                              style={{ fontFamily: 'Lato, sans-serif' }}
                            >
                              {typeof member.family === 'object' && member.family ? member.family.familyName : 'N/A'}
                            </TableCell>
                            <TableCell
                              className="px-4 py-3 text-gray-500 text-theme-sm dark:text-gray-400"
                              style={{ fontFamily: 'Lato, sans-serif' }}
                            >
                              {member.dob ? dayjs(member.dob).format('DD MMM YYYY') : 'N/A'}
                            </TableCell>
                            <TableCell
                              className="px-4 py-3 text-gray-500 text-theme-sm dark:text-gray-400"
                              style={{ fontFamily: 'Lato, sans-serif' }}
                            >
                              {member.education || 'N/A'}
                            </TableCell>
                            <TableCell
                              className="px-4 py-3 text-gray-500 text-theme-sm dark:text-gray-400"
                              style={{ fontFamily: 'Lato, sans-serif' }}
                            >
                              {member.job || 'N/A'}
                            </TableCell>
                            <TableCell
                              className="px-4 py-3 text-gray-500 text-theme-sm dark:text-gray-400"
                              style={{ fontFamily: 'Lato, sans-serif' }}
                            >
                              {member.contactNumbers && member.contactNumbers.length > 0 ? member.contactNumbers.join(', ') : 'N/A'}
                            </TableCell>
                            <TableCell
                              className="px-4 py-3 text-gray-500 text-theme-sm dark:text-gray-400"
                              style={{ fontFamily: 'Lato, sans-serif' }}
                            >
                              {dayjs(member.createdAt).format('DD MMM YYYY')}
                            </TableCell>
                            <TableCell className="px-4 py-3 text-gray-500 text-theme-sm dark:text-gray-400">
                              <div onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                                <Popover
                                  placement="top"
                                  aria-labelledby="default-popover"
                                  content={
                                    <div className="py-2 px-3">
                                      <div
                                        className="flex items-center gap-3 px-3 py-2 font-medium text-gray-700 rounded-lg group text-theme-sm hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300 cursor-pointer"
                                        onClick={() => {
                                          setSelectedMember(member._id);
                                          setOpenModal(true);
                                        }}
                                        style={{ fontFamily: 'Lato, sans-serif' }}
                                      >
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="24"
                                          height="24"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          className="lucide lucide-trash-2"
                                        >
                                          <path d="M3 6h18" />
                                          <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                                          <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                                          <line x1="10" x2="10" y1="11" y2="17" />
                                          <line x1="14" x2="14" y1="11" y2="17" />
                                        </svg>
                                        Delete
                                      </div>

                                      <div
                                        className="flex items-center gap-3 px-3 py-2 font-medium text-gray-700 rounded-lg group text-theme-sm hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300 cursor-pointer"
                                        onClick={() => handleEdit(member)}
                                        style={{ fontFamily: 'Lato, sans-serif' }}
                                      >
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="24"
                                          height="24"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          className="lucide lucide-pencil"
                                        >
                                          <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5 5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" />
                                          <path d="m15 5 4 4" />
                                        </svg>
                                        Edit
                                      </div>
                                    </div>
                                  }
                                >
                                  <button
                                    className="flex items-center justify-center w-10 h-10 text-gray-700 rounded-lg z-99999 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800 transition-colors"
                                    style={{ fontFamily: 'Lato, sans-serif' }}
                                  >
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M5.99902 10.4951C6.82745 10.4951 7.49902 11.1667 7.49902 11.9951V12.0051C7.49902 12.8335 6.82745 13.5051 5.99902 13.5051C5.1706 13.5051 4.49902 12.8335 4.49902 12.0051V11.9951C4.49902 11.1667 5.1706 10.4951 5.99902 10.4951ZM17.999 10.4951C18.8275 10.4951 19.499 11.1667 19.499 11.9951V12.0051C19.499 12.8335 18.8275 13.5051 17.999 13.5051C17.1706 13.5051 16.499 12.8335 16.499 12.0051V11.9951C16.499 11.1667 17.1706 10.4951 17.999 10.4951ZM13.499 11.9951C13.499 11.1667 12.8275 10.4951 11.999 10.4951C11.1706 10.4951 10.499 11.1667 10.499 11.9951V12.0051C10.499 12.8335 11.1706 13.5051 11.999 13.5051C12.8275 13.5051 13.499 12.8335 13.499 12.0051V11.9951Z"
                                        fill="currentColor"
                                      />
                                    </svg>
                                  </button>
                                </Popover>
                              </div>
                            </TableCell>
                          </div>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 px-4 py-3 border-t border-gray-200 dark:border-white/[0.05]">
              <div className="text-sm text-gray-500 dark:text-gray-400" style={{ fontFamily: 'Lato, sans-serif' }}>
                Showing {(pagination.page - 1) * pagination.limit + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                {pagination.total} results
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  color="light"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={!pagination.hasPrevPage || loading}
                  style={{ fontFamily: 'Lato, sans-serif' }}
                >
                  Previous
                </Button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    let pageNum: number;
                    if (pagination.totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.page <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.page >= pagination.totalPages - 2) {
                      pageNum = pagination.totalPages - 4 + i;
                    } else {
                      pageNum = pagination.page - 2 + i;
                    }
                    return (
                      <Button
                        key={pageNum}
                        size="sm"
                        color={pagination.page === pageNum ? 'warning' : 'light'}
                        onClick={() => setCurrentPage(pageNum)}
                        disabled={loading}
                        style={{ fontFamily: 'Lato, sans-serif', minWidth: '40px' }}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                <Button
                  size="sm"
                  color="light"
                  onClick={() => setCurrentPage((p) => Math.min(pagination.totalPages, p + 1))}
                  disabled={!pagination.hasNextPage || loading}
                  style={{ fontFamily: 'Lato, sans-serif' }}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </ComponentCard>
      </div>
    </>
  );
}
